import { MultiButtons } from '@/gf/components/MultiButton'
import { completesEditQuote, initiatesEditQuote } from '@/gf/modules/GrammarEvents'
import { cleanGraphQlInput } from '@/gf/modules/GraphQL'
import MoneyM from '@/gf/modules/Money'
import TimeM from '@/gf/modules/Time'
import MachineInformation from '../MachineInformation'
import QuoteBuilder from '../QuoteBuilder'
import StepNote from '../StepNote'
import { viewOriginalRequestAction } from '../ViewOriginalRequest'
import { useRfqContext } from '../context'
import QuoteUploadBar from '../QuoteUpload/QuoteUploadBar'
import Comments from '../Comments'
import { useMemo } from 'react'
import { deletePersistedForm } from '../QuoteBuilder/useForm'
import Id from '@/gf/modules/Id'
import { DocumentUploadContextProps, FormItem } from '../types'
import matchUploadedLineItems from './matchUploadedLineItems'

const EditQuote = ({
  quoteUpload,
  exitEditMode,
  onCreateShipping,
  onReadyForPickup,
  onMarkPickedUp,
  onAcceptQuote,
  onFormItemsChange,
  ...props
}: {
  quoteUpload: DocumentUploadContextProps
  onQuoteSuccess: () => Promise<void>
  exitEditMode: () => void
  onCreateShipping: () => void
  onReadyForPickup: () => void
  onMarkPickedUp: () => void
  onAcceptQuote: () => void
  onFormItemsChange?: (items: FormItem[]) => void
}) => {
  const { rfq, invoiceSent } = useRfqContext()

  const storageKey = useMemo(() => (rfq.quote ? Id.shorten(rfq.quote.id) : ''), [rfq.quote])

  // Prefer context lineItems (from new Document model) over existing quote
  const hasContextLineItems = quoteUpload.lineItems.length > 0

  // Clear localStorage when AI corrections are applied so form reinitializes with new values
  if (hasContextLineItems && storageKey) {
    deletePersistedForm(storageKey)
  }

  if (!rfq.quote) return null

  // Priority: 1) context line items (new Document model), 2) existing quote line items
  const items = hasContextLineItems
    ? matchUploadedLineItems(quoteUpload.lineItems, rfq.quote.lineItems)
    : rfq.quote.lineItems.map((li) => ({
        id: li.id,
        partNumber: li.product.mpn,
        // Existing quote line items don't surface alt identifiers in the
        // editor — the dealer is editing identifiers, not re-confirming alts.
        altPartNumbers: [],
        description: li.product.name,
        unitPrice: MoneyM.toDecimal(li.unitPrice),
        quantity: li.quantity,
        inStock: li.inStock ?? true,
        externalId: li.externalId,
        taskNumber: li.taskNumber,
        suggestion: li.suggestion,
        availabilityDate: li.availableAt ? TimeM.toPayload(li.availableAt) : null,
        rfqPartId: li.rfqPart?.id ?? null,
        rfqPartNumber: li.rfqPart?.mpn ?? null,
      }))

  const savedShippingCost = rfq.quote.shippingAmount
    ? MoneyM.toDecimal(rfq.quote.shippingAmount)
    : null
  const savedTaxCost = MoneyM.toDecimal(rfq.quote.salesTax)

  const initialValues = {
    customerContactId: null,
    // Prefer extracted value when present. `null` from the extractor means
    // "not on the doc" — fall back to the existing saved quote value.
    shippingCost:
      hasContextLineItems && quoteUpload.shippingCost !== null
        ? quoteUpload.shippingCost
        : savedShippingCost,
    taxCost:
      hasContextLineItems && quoteUpload.taxCost !== null ? quoteUpload.taxCost : savedTaxCost,
    customerDiscount: rfq.quote.discounts.reduce((sum, { percent }) => sum + percent, 0),
    quoteNumber: rfq.quote.quoteNumber,
    deliveryMethod: rfq.quote.deliveryMethod,
    items,
    pickupAddress: rfq.quote.pickupAddress ? cleanGraphQlInput(rfq.quote.pickupAddress) : null,
    shippingAddress: rfq.quote.shippingAddress
      ? cleanGraphQlInput(rfq.quote.shippingAddress)
      : null,
    comments: null,
    neededByDate: null,
    urgency: null,
  }

  const onQuoteSuccess = (storeOrderId: string) => {
    props.onQuoteSuccess()
    completesEditQuote({ rfqId: rfq.id, storeOrderId })
  }

  return (
    <QuoteBuilder
      key={hasContextLineItems ? `document-v${quoteUpload.lineItemsVersion}` : 'initial'}
      storeOrderId={rfq.quote.id}
      initialValues={initialValues}
      images={rfq.images.map((i) => i.url)}
      machineDetailsSection={
        <>
          <MachineInformation
            reqMachine={rfq.machines[0] ?? null}
            className="px-4"
            hideUnitNumber={rfq.user?.organization?.hideUnitNumberFromVendors ?? false}
          />

          {rfq.partsRequest && <Comments partsRequest={rfq.partsRequest} />}
        </>
      }
      onSuccess={onQuoteSuccess}
      onCancel={exitEditMode}
      actions={
        <QuoteUploadBar
          onError={quoteUpload.onError}
          onUploadComplete={quoteUpload.onUploadComplete}
          onUploadStarted={quoteUpload.onUploadStarted}
          rfqId={rfq.id}
          hasQuoteUpload={rfq.hasQuoteUpload}
        />
      }
      sideNote={
        <StepNote
          rfq={rfq}
          onCreateShipping={onCreateShipping}
          onReadyForPickup={onReadyForPickup}
          onMarkPickedUp={onMarkPickedUp}
          invoiceSent={invoiceSent}
          onAcceptQuote={onAcceptQuote}
        />
      }
      secondaryActions={
        <MultiButtons className="!shadow-sm" items={[viewOriginalRequestAction(rfq)]} />
      }
      showSalesTax={rfq.requestDetails !== null}
      customerFeeRate={rfq.quote.customerFeeRate}
      canSetDeliveryMethod={!!rfq.requestDetails}
      onTouched={() => initiatesEditQuote({ rfqId: rfq.id, storeOrderId: rfq.quote?.id })}
      showWhenCustomerDiscountAutoFilled={false}
      onItemsChange={onFormItemsChange}
    />
  )
}

export default EditQuote
